import type { MakeupSearchFilters } from "@/lib/services/makeupService";
import { MakeupStatus } from "@/openapi/generated";
import { formatMakeupDateOnly } from "./makeupDates";
import {
  MAKEUP_STATUS_FILTER_ALL,
  makeupStatusFilterOptions,
} from "./makeupStatusFilters";

export interface MakeupRequestFiltersState {
  status: string;
  firstName: string;
  lastName: string;
  /** Legacy deep links; prefer first/last name search. */
  studentId: number | null;
  sourceClassId: number | null;
  makeupClassId: number | null;
  locationId: number | null;
  fromDate: string;
  toDate: string;
}

export type MakeupRequestFilterChipKey =
  | keyof MakeupRequestFiltersState
  | "quickFirstName"
  | "quickLastName";

export interface MakeupRequestFilterChip {
  key: MakeupRequestFilterChipKey;
  label: string;
}

export const EMPTY_MAKEUP_REQUEST_FILTERS: MakeupRequestFiltersState = {
  status: MAKEUP_STATUS_FILTER_ALL,
  firstName: "",
  lastName: "",
  studentId: null,
  sourceClassId: null,
  makeupClassId: null,
  locationId: null,
  fromDate: "",
  toDate: "",
};

const VALID_STATUS_VALUES = new Set([
  MAKEUP_STATUS_FILTER_ALL,
  ...Object.values(MakeupStatus),
]);

function parseOptionalInt(value: string | null): number | null {
  if (value == null || value.trim() === "") return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseStatus(value: string | null): string {
  if (value == null || !VALID_STATUS_VALUES.has(value)) {
    return MAKEUP_STATUS_FILTER_ALL;
  }
  return value;
}

const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Keep filter dates as calendar days. Display formatting is not a valid filter value. */
function parseDateString(value: string | null): string {
  if (!value?.trim()) return "";
  const trimmed = value.trim();
  const match = CALENDAR_DAY.exec(trimmed);
  if (!match) return "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(year, month - 1, day, 12, 0, 0, 0);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return "";
  }
  return trimmed;
}

function formatFilterDateLabel(ymd: string): string {
  return formatMakeupDateOnly(ymd) || ymd;
}

export function makeupRequestFiltersEqual(
  a: MakeupRequestFiltersState,
  b: MakeupRequestFiltersState,
): boolean {
  return (
    a.status === b.status &&
    a.firstName === b.firstName &&
    a.lastName === b.lastName &&
    a.studentId === b.studentId &&
    a.sourceClassId === b.sourceClassId &&
    a.makeupClassId === b.makeupClassId &&
    a.locationId === b.locationId &&
    a.fromDate === b.fromDate &&
    a.toDate === b.toDate
  );
}

export function countMakeupRequestFilters(
  filters: MakeupRequestFiltersState,
): number {
  let count = 0;
  if (filters.status !== MAKEUP_STATUS_FILTER_ALL) count++;
  if (filters.firstName.trim()) count++;
  if (filters.lastName.trim()) count++;
  if (filters.studentId != null) count++;
  if (filters.sourceClassId != null) count++;
  if (filters.makeupClassId != null) count++;
  if (filters.locationId != null) count++;
  if (filters.fromDate.trim()) count++;
  if (filters.toDate.trim()) count++;
  return count;
}

/** Modal badge count — excludes names owned by quick search. */
export function countMakeupModalFilters(
  filters: MakeupRequestFiltersState,
  quickFirstName = "",
  quickLastName = "",
): number {
  let count = countMakeupRequestFilters(filters);
  if (quickFirstName.trim() && filters.firstName.trim()) {
    count -= 1;
  }
  if (quickLastName.trim() && filters.lastName.trim()) {
    count -= 1;
  }
  return count;
}

export function hasActiveMakeupRequestFilters(
  filters: MakeupRequestFiltersState,
): boolean {
  return countMakeupRequestFilters(filters) > 0;
}

export function hasActiveMakeupRequestSearch(
  quickFirstName: string,
  quickLastName: string,
  filters: MakeupRequestFiltersState,
): boolean {
  return (
    quickFirstName.trim() !== "" ||
    quickLastName.trim() !== "" ||
    hasActiveMakeupRequestFilters(filters)
  );
}

export function parseMakeupRequestFiltersFromSearchParams(
  searchParams: URLSearchParams,
): MakeupRequestFiltersState {
  return {
    status: parseStatus(searchParams.get("status")),
    firstName: searchParams.get("firstName")?.trim() ?? "",
    lastName: searchParams.get("lastName")?.trim() ?? "",
    studentId: parseOptionalInt(searchParams.get("studentId")),
    sourceClassId: parseOptionalInt(searchParams.get("sourceClassId")),
    makeupClassId: parseOptionalInt(searchParams.get("makeupClassId")),
    locationId: parseOptionalInt(searchParams.get("locationId")),
    fromDate: parseDateString(searchParams.get("fromDate")),
    toDate: parseDateString(searchParams.get("toDate")),
  };
}

/** @deprecated Use buildMakeupRequestsPageQuery from makeupRequestsPageUrl */
export function buildMakeupRequestFiltersQuery(
  filters: MakeupRequestFiltersState,
  page = 1,
  lastSelectedId?: string,
): string {
  const params = new URLSearchParams();

  if (filters.firstName.trim()) {
    params.set("firstName", filters.firstName.trim());
  }
  if (filters.lastName.trim()) {
    params.set("lastName", filters.lastName.trim());
  }
  if (filters.status !== MAKEUP_STATUS_FILTER_ALL) {
    params.set("status", filters.status);
  }
  if (filters.studentId != null) {
    params.set("studentId", String(filters.studentId));
  }
  if (filters.sourceClassId != null) {
    params.set("sourceClassId", String(filters.sourceClassId));
  }
  if (filters.makeupClassId != null) {
    params.set("makeupClassId", String(filters.makeupClassId));
  }
  if (filters.locationId != null) {
    params.set("locationId", String(filters.locationId));
  }
  if (filters.fromDate.trim()) {
    params.set("fromDate", filters.fromDate.trim());
  }
  if (filters.toDate.trim()) {
    params.set("toDate", filters.toDate.trim());
  }
  if (page > 1) {
    params.set("page", String(page));
  }
  if (page > 1 && lastSelectedId) {
    params.set("lastSelectedId", lastSelectedId);
  }

  const query = params.toString();
  return query ? `?${query}` : "";
}

/** @deprecated Use makeupRequestsPageUrlMatchesState */
export function makeupRequestFiltersMatchSearchParams(
  filters: MakeupRequestFiltersState,
  page: number,
  lastSelectedId: string | undefined,
  searchParams: URLSearchParams,
): boolean {
  const fromUrl = parseMakeupRequestFiltersFromSearchParams(searchParams);
  if (!makeupRequestFiltersEqual(filters, fromUrl)) return false;

  const pageRaw = searchParams.get("page");
  const pageFromUrl = pageRaw != null ? Number.parseInt(pageRaw, 10) : 1;
  const normalizedPage =
    Number.isFinite(pageFromUrl) && pageFromUrl >= 1 ? pageFromUrl : 1;

  if (normalizedPage !== page) return false;
  if (page <= 1) return true;
  return searchParams.get("lastSelectedId") === lastSelectedId;
}

function toApiDate(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = new Date(`${trimmed}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function makeupRequestFiltersToApi(
  filters: MakeupRequestFiltersState,
  teacherId?: number | null,
  studentIdOverride?: number | null,
): MakeupSearchFilters {
  const studentId =
    studentIdOverride !== undefined ? studentIdOverride : filters.studentId;

  return {
    memberId: null,
    status:
      filters.status === MAKEUP_STATUS_FILTER_ALL
        ? null
        : (filters.status as MakeupStatus),
    studentId,
    sourceClassId: filters.sourceClassId,
    makeupClassId: filters.makeupClassId,
    locationId: filters.locationId,
    fromDate: toApiDate(filters.fromDate),
    toDate: toApiDate(filters.toDate),
    teacherId: teacherId ?? null,
  };
}

export function formatMakeupRequestsListDescription(
  totalCount: number,
  quickFirstName: string,
  quickLastName: string,
  filters: MakeupRequestFiltersState,
): string {
  const criteriaCount = countActiveMakeupCriteria(
    quickFirstName,
    quickLastName,
    filters,
  );
  const matchWord = totalCount === 1 ? "request" : "requests";

  if (criteriaCount === 0) {
    return "Review parent makeup requests, check class capacity, and approve or propose another time.";
  }

  return `${totalCount} ${matchWord} matching ${criteriaCount} search ${
    criteriaCount === 1 ? "criterion" : "criteria"
  }.`;
}

function countActiveMakeupCriteria(
  quickFirstName: string,
  quickLastName: string,
  filters: MakeupRequestFiltersState,
): number {
  let count = countMakeupRequestFilters(filters);
  if (quickFirstName.trim() && !filters.firstName.trim()) {
    count += 1;
  }
  if (quickLastName.trim() && !filters.lastName.trim()) {
    count += 1;
  }
  return count;
}

export function buildMakeupRequestFilterChips(
  quickFirstName: string,
  quickLastName: string,
  filters: MakeupRequestFiltersState,
  labels: {
    sourceClassName?: string | null;
    makeupClassName?: string | null;
    locationName?: string | null;
  },
): MakeupRequestFilterChip[] {
  const chips: MakeupRequestFilterChip[] = [];

  if (quickFirstName.trim()) {
    chips.push({
      key: "quickFirstName",
      label: `First name: ${quickFirstName.trim()}`,
    });
  } else if (filters.firstName.trim()) {
    chips.push({
      key: "firstName",
      label: `First name: ${filters.firstName.trim()}`,
    });
  }

  if (quickLastName.trim()) {
    chips.push({
      key: "quickLastName",
      label: `Last name: ${quickLastName.trim()}`,
    });
  } else if (filters.lastName.trim()) {
    chips.push({
      key: "lastName",
      label: `Last name: ${filters.lastName.trim()}`,
    });
  }

  if (filters.status !== MAKEUP_STATUS_FILTER_ALL) {
    const statusLabel = makeupStatusFilterOptions().find(
      (option) => option.value === filters.status,
    )?.label;
    chips.push({
      key: "status",
      label: `Status: ${statusLabel ?? filters.status}`,
    });
  }

  if (filters.studentId != null) {
    chips.push({
      key: "studentId",
      label: `Student #${filters.studentId}`,
    });
  }

  if (filters.sourceClassId != null) {
    chips.push({
      key: "sourceClassId",
      label: `Missed class: ${labels.sourceClassName ?? `#${filters.sourceClassId}`}`,
    });
  }

  if (filters.makeupClassId != null) {
    chips.push({
      key: "makeupClassId",
      label: `Makeup class: ${labels.makeupClassName ?? `#${filters.makeupClassId}`}`,
    });
  }

  if (filters.locationId != null) {
    chips.push({
      key: "locationId",
      label: `Location: ${labels.locationName ?? `#${filters.locationId}`}`,
    });
  }

  if (filters.fromDate.trim()) {
    chips.push({
      key: "fromDate",
      label: `From: ${formatFilterDateLabel(filters.fromDate)}`,
    });
  }

  if (filters.toDate.trim()) {
    chips.push({
      key: "toDate",
      label: `To: ${formatFilterDateLabel(filters.toDate)}`,
    });
  }

  return chips;
}
